import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const [amount, setAmount] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 6;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setState("timeout");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount_total ? data.amount_total / 100 : null);
          setState("success");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="checkout-status"
        className="max-w-lg w-full border border-white/10 bg-[#1F1F1F] p-10 text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-[#4ADE80] mx-auto" />
            <h1 className="mt-8 font-display font-black text-3xl uppercase tracking-tighter">Verifying Payment</h1>
            <p className="mt-3 font-mono-accent text-sm text-[#7a7a7a]">Confirming with the payment processor…</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-[#4ADE80] mx-auto" />
            <h1 data-testid="success-heading" className="mt-8 font-display font-black text-4xl uppercase tracking-tighter">Order Confirmed</h1>
            <p className="mt-3 text-[#A8A8A8]">Thank you for your order. A confirmation and COA package will be sent to your email.</p>
            {amount != null && <p className="mt-6 font-display font-black text-3xl text-[#4ADE80]">{fmtPrice(amount)}</p>}
            <Link to="/products" className="mt-10 inline-block bg-[#4ADE80] text-[#F5F5F5] font-display font-bold uppercase tracking-tight px-8 py-4 hover:bg-[#0F0F10] transition-colors">
              Continue Browsing
            </Link>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="h-12 w-12 text-[#EF4444] mx-auto" />
            <h1 className="mt-8 font-display font-black text-3xl uppercase tracking-tighter">
              {state === "timeout" ? "Still Processing" : "Payment Issue"}
            </h1>
            <p className="mt-3 text-[#A8A8A8]">
              {state === "timeout"
                ? "Your payment is taking longer than expected. Check your email for confirmation."
                : "We couldn't confirm your payment. No charge may have been made."}
            </p>
            <Link to="/cart" className="mt-10 inline-block border border-white/10 font-display font-bold uppercase tracking-tight px-8 py-4 hover:bg-[#0F0F10] hover:text-[#F5F5F5] transition-colors">
              Back to Cart
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
